import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import Errors from '../../../shared/validation/errors';
import headersBuilder, { Method, buildPublicGetRequest } from '../../../utils/request';
import { cacheAndSetColorsFromWebSettings } from '../../../utils/customColorSetter';
import { cacheWebCommonSettings } from '../../../utils/webCommonSettingsCache';
import { cacheWebIframeSettings } from '../../../utils/webIframeSettingsCache';
import { WebColorsSettings, WebCommonSettings, WebIframeSettings } from '../../../types/settingsTypes';

export const FETCH_WEB_COMMON_SETTINGS = 'FETCH_WEB_COMMON_SETTINGS';
export const FETCH_WEB_COLORS_SETTINGS = 'FETCH_WEB_COLORS_SETTINGS';
export const FETCH_WEB_IFRAME_SETTINGS = 'FETCH_WEB_IFRAME_SETTINGS';

export type FetchWebCommonSettingsAction = {
  type: string;
  settings: WebCommonSettings;
};

export type FetchWebColorsSettingsAction = {
  type: string;
  settings: WebColorsSettings;
};

export type FetchWebIframeSettingsAction = {
  type: string;
  settings: WebIframeSettings;
};

const normalizeWebColorsSettings = (settings: Partial<WebColorsSettings> | null | undefined): WebColorsSettings => {
  return {
    ...new WebColorsSettings(),
    ...(settings || {})
  };
};

const fetchWebCommonSettings = (settings: WebCommonSettings): FetchWebCommonSettingsAction => {
  return {
    type: FETCH_WEB_COMMON_SETTINGS,
    settings
  };
};

const fetchWebColorsSettings = (settings: WebColorsSettings): FetchWebColorsSettingsAction => {
  return {
    type: FETCH_WEB_COLORS_SETTINGS,
    settings
  };
};

const fetchWebIframeSettings = (settings: WebIframeSettings): FetchWebIframeSettingsAction => {
  return {
    type: FETCH_WEB_IFRAME_SETTINGS,
    settings
  };
};

export const fetchWebCommonSettingsAsync = (api: string) => {
  return (dispatch: Dispatch): void => {
    if (!api) {
      return;
    }

    fetch(`${api}/settings/web`, buildPublicGetRequest())
      .then((response: Response) => {
        if (!response.ok) {
          return null;
        }
        return response.json();
      })
      .then((settings: Partial<WebCommonSettings> | null) => {
        if (!settings) {
          return;
        }

        dispatch(fetchWebCommonSettings(cacheWebCommonSettings(settings)));
      })
      .catch(() => {
        // Ignore errors: keep current frontend values.
      });
  };
};

export const editWebCommonSettingsAsync = (api: string, request: WebCommonSettings) => {
  return (dispatch: Dispatch): void => {
    fetch(`${api}/settings/web`, headersBuilder(Method.POST, request))
      .then((response: Response) => Errors.checkResponseForErrors(response, api))
      .then((settings: any) => {
        dispatch(fetchWebCommonSettings(cacheWebCommonSettings(settings)));
        toast.success('Web common settings edited successfully!', {
          position: toast.POSITION.BOTTOM_RIGHT
        });
      })
      .catch((error: Error) => Errors.throwErrorMessage(error));
  };
};

export const fetchWebColorsSettingsAsync = (api: string) => {
  return (dispatch: Dispatch): void => {
    if (!api) {
      return;
    }

    fetch(`${api}/settings/web/colors`, buildPublicGetRequest())
      .then((response: Response) => {
        if (!response.ok) {
          return null;
        }
        return response.json();
      })
      .then((settings: Partial<WebColorsSettings> | null) => {
        if (!settings) {
          return;
        }

        const normalizedSettings = normalizeWebColorsSettings(settings);
        dispatch(fetchWebColorsSettings(normalizedSettings));
        cacheAndSetColorsFromWebSettings(normalizedSettings);
      })
      .catch(() => {
        // Ignore errors: keep current frontend values.
      });
  };
};

export const editWebColorsSettingsAsync = (api: string, request: WebColorsSettings) => {
  return (dispatch: Dispatch): void => {
    fetch(`${api}/settings/web/colors`, headersBuilder(Method.POST, request))
      .then((response: Response) => Errors.checkResponseForErrors(response, api))
      .then((settings: any) => {
        const normalizedSettings = normalizeWebColorsSettings(settings);

        dispatch(fetchWebColorsSettings(normalizedSettings));
        cacheAndSetColorsFromWebSettings(normalizedSettings);
        toast.success('Web colors settings edited successfully!', {
          position: toast.POSITION.BOTTOM_RIGHT
        });
      })
      .catch((error: Error) => Errors.throwErrorMessage(error));
  };
};

export const fetchWebIframeSettingsAsync = (api: string) => {
  return (dispatch: Dispatch): void => {
    if (!api) {
      return;
    }

    fetch(`${api}/settings/web/iframe`, buildPublicGetRequest())
      .then((response: Response) => {
        if (!response.ok) {
          return null;
        }
        return response.json();
      })
      .then((settings: Partial<WebIframeSettings> | null) => {
        if (!settings) {
          return;
        }

        dispatch(fetchWebIframeSettings(cacheWebIframeSettings(settings)));
      })
      .catch(() => {
        // Ignore errors: keep current frontend values.
      });
  };
};

export const editWebIframeSettingsAsync = (api: string, request: WebIframeSettings) => {
  return (dispatch: Dispatch): void => {
    fetch(`${api}/settings/web/iframe`, headersBuilder(Method.POST, request))
      .then((response: Response) => Errors.checkResponseForErrors(response, api))
      .then((settings: any) => {
        dispatch(fetchWebIframeSettings(cacheWebIframeSettings(settings)));
        toast.success('Web iframe settings edited successfully!', {
          position: toast.POSITION.BOTTOM_RIGHT
        });
      })
      .catch((error: Error) => Errors.throwErrorMessage(error));
  };
};
